import type { FC, MouseEvent } from 'react';
import { Link } from 'react-router-dom';
// mantine
import {
    Avatar,
    Indicator,
    Menu,
    MenuDivider,
    Tooltip,
    UnstyledButton
} from '@mantine/core';
// icons
import {
    ChevronDown,
    CircleX,
    HelpCircle,
    Mail,
    LogOut,
    Users,
    User,
    SquarePlay
} from 'lucide-react';
// utils
import { base64ToImageSource } from '@newo/toolkit/image';
import { SOCIALS, SUPPORT_EMAIL } from '@newo/constants';
import type { TProfile } from '@newo/api/gateway';
import { PATHS } from '@/const';
import useAppStore from '@/store/app';

interface UserMenuProps {
    profile?: TProfile;
}

const UserMenu: FC<UserMenuProps> = ({ profile }) => {
    const logout = useAppStore((state) => state.logout);

    const handleLogout = (event: MouseEvent<HTMLButtonElement>) => {
        event.preventDefault();
        logout();
    };

    const isActive = profile ? profile.is_active : true;

    return (
        <Menu withArrow arrowPosition="side">
            <Menu.Target>
                <UnstyledButton className="flex items-center gap-2 text-sm whitespace-nowrap">
                    <Tooltip
                        arrowPosition="side"
                        disabled={isActive}
                        label="Account deactivated"
                        offset={10}
                        position="right"
                    >
                        <Indicator
                            color="red"
                            disabled={isActive}
                            label={<CircleX />}
                            offset={10}
                            size={16}
                        >
                            <Avatar
                                className="shrink-0 "
                                color="blue"
                                radius="xl"
                                size="2rem"
                                src={
                                    profile?.logo
                                        ? base64ToImageSource(profile.logo)
                                        : undefined
                                }
                            >
                                {(profile?.last_name ?? '')
                                    .at(0)
                                    ?.toUpperCase()}
                            </Avatar>
                        </Indicator>
                    </Tooltip>
                    Profile
                    <ChevronDown />
                </UnstyledButton>
            </Menu.Target>
            <Menu.Dropdown>
                <Menu.Item
                    component={Link}
                    leftSection={<User />}
                    to={PATHS.profile.account}
                >
                    Profile
                </Menu.Item>
                <Menu.Item
                    component={Link}
                    leftSection={<Users />}
                    to={PATHS.profile.members}
                >
                    Members
                </Menu.Item>
                <MenuDivider />
                <Menu.Label>Help</Menu.Label>
                <Menu.Item
                    component={Link}
                    leftSection={<SquarePlay />}
                    target="_blank"
                    to={SOCIALS.youtube}
                >
                    Video Tutorials
                </Menu.Item>
                <Menu.Item
                    component={Link}
                    leftSection={<HelpCircle />}
                    target="_blank"
                    to={SOCIALS.docs}
                >
                    Docs
                </Menu.Item>
                <Menu.Label>Email support</Menu.Label>
                <Menu.Item
                    component={Link}
                    leftSection={<Mail />}
                    target="_blank"
                    to={`mailto:${SUPPORT_EMAIL}`}
                >
                    {SUPPORT_EMAIL}
                </Menu.Item>
                <MenuDivider />
                <Menu.Item
                    color="red"
                    leftSection={<LogOut />}
                    onClick={handleLogout}
                >
                    Logout
                </Menu.Item>
            </Menu.Dropdown>
        </Menu>
    );
};

export default UserMenu;
